import {
  memo, useLayoutEffect, useRef,
} from '../../../lib/teact/teact';
import { getActions } from '../../../global';

import type { ApiPhoto } from '../../../api/types';
import { MediaViewerOrigin } from '../../../types';

import buildClassName from '../../../util/buildClassName';

import useLastCallback from '../../../hooks/useLastCallback';

import PeerColorWrapper from '../PeerColorWrapper';
import CodeOverlay from './CodeOverlay';
import { getDiagramLayout } from './mermaidLayout';

import styles from './MermaidBlock.module.scss';

type OwnProps = {
  text: string;
  svgDataUrl?: string; // Server-rendered SVG data URI (data:image/svg+xml;base64,...)
  noCopy?: boolean;
};

function detectTheme() {
  const html = document.documentElement;
  if (html.classList.contains('theme-dark')) return 'dark';
  return 'default';
}

function svgElementToBlobUrl(svgEl: SVGSVGElement): { url: string; width: number; height: number } {
  const clone = svgEl.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

  const viewBox = clone.getAttribute('viewBox');
  let width = 800;
  let height = 600;
  if (viewBox) {
    const parts = viewBox.split(/\s+/);
    if (parts.length >= 4) {
      width = Math.round(parseFloat(parts[2]));
      height = Math.round(parseFloat(parts[3]));
    }
  }
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  clone.removeAttribute('style');

  const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bgRect.setAttribute('width', '100%');
  bgRect.setAttribute('height', '100%');
  bgRect.setAttribute('fill', detectTheme() === 'dark' ? '#212121' : 'white');
  clone.insertBefore(bgRect, clone.firstChild);

  const xml = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([xml], { type: 'image/svg+xml' });
  return { url: URL.createObjectURL(blob), width, height };
}

const NODE_PADDING_X = 16;
const NODE_PADDING_Y = 8;

/**
 * Fix Mermaid's CJK text-width underestimation.
 * Server-side mermaid measures text width without a real font renderer,
 * so CJK characters (which are ~2x wider than ASCII) get undersized rects.
 * Walk all .node groups, measure actual text bbox, and resize rects/polygons.
 */
function fixCjkNodeSizes(svg: SVGSVGElement) {
  const nodes = svg.querySelectorAll('.node');
  nodes.forEach((node) => {
    const labels = node.querySelectorAll('.label');
    if (!labels.length) return;

    // Measure the actual rendered text bounding box
    let maxTextWidth = 0;
    let maxTextHeight = 0;
    labels.forEach((label) => {
      const texts = label.querySelectorAll('text, foreignObject');
      texts.forEach((t) => {
        const bbox = (t as SVGGraphicsElement).getBBox?.();
        if (bbox) {
          maxTextWidth = Math.max(maxTextWidth, bbox.width);
          maxTextHeight = Math.max(maxTextHeight, bbox.height);
        }
      });
    });

    if (maxTextWidth <= 0) return;

    const neededWidth = maxTextWidth + NODE_PADDING_X * 2;
    const neededHeight = maxTextHeight + NODE_PADDING_Y * 2;

    // Resize rect elements (standard nodes)
    const rect = node.querySelector('rect');
    if (rect) {
      const curWidth = parseFloat(rect.getAttribute('width') || '0');
      const curHeight = parseFloat(rect.getAttribute('height') || '0');
      if (neededWidth > curWidth) {
        const dx = (neededWidth - curWidth) / 2;
        rect.setAttribute('width', String(neededWidth));
        const curX = parseFloat(rect.getAttribute('x') || '0');
        rect.setAttribute('x', String(curX - dx));
      }
      if (neededHeight > curHeight) {
        const dy = (neededHeight - curHeight) / 2;
        rect.setAttribute('height', String(neededHeight));
        const curY = parseFloat(rect.getAttribute('y') || '0');
        rect.setAttribute('y', String(curY - dy));
      }
    }

    // Resize polygon elements (diamond/rhombus decision nodes)
    const polygon = node.querySelector('polygon');
    if (polygon && !rect) {
      const points = polygon.getAttribute('points');
      if (points) {
        const pts = points.split(/[\s,]+/).map(Number);
        if (pts.length >= 8) {
          // Diamond: 4 points (top, right, bottom, left)
          // Find current bounding box
          let minX = Infinity; let maxX = -Infinity;
          let minY = Infinity; let maxY = -Infinity;
          for (let i = 0; i < pts.length; i += 2) {
            minX = Math.min(minX, pts[i]);
            maxX = Math.max(maxX, pts[i]);
            minY = Math.min(minY, pts[i + 1]);
            maxY = Math.max(maxY, pts[i + 1]);
          }
          const curW = maxX - minX;
          const curH = maxY - minY;
          // Diamonds need ~1.4x text size due to 45° angles
          const diamondW = neededWidth * 1.42;
          const diamondH = neededHeight * 1.42;
          if (diamondW > curW || diamondH > curH) {
            const cx = (minX + maxX) / 2;
            const cy = (minY + maxY) / 2;
            const hw = Math.max(curW, diamondW) / 2;
            const hh = Math.max(curH, diamondH) / 2;
            polygon.setAttribute('points',
              `${cx},${cy - hh} ${cx + hw},${cy} ${cx},${cy + hh} ${cx - hw},${cy}`);
          }
        }
      }
    }
  });
}

const MermaidBlock = ({ text, svgDataUrl, noCopy }: OwnProps) => {
  const { openMediaViewer } = getActions();
  const svgRef = useRef<HTMLDivElement>();

  // Decode server-rendered SVG from data URI (must handle UTF-8 properly)
  const serverSvg = svgDataUrl ? (() => {
    try {
      const base64 = svgDataUrl.replace('data:image/svg+xml;base64,', '');
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new TextDecoder().decode(bytes);
    } catch {
      return undefined;
    }
  })() : undefined;

  useLayoutEffect(() => {
    if (!svgRef.current || !serverSvg) return;
    svgRef.current.innerHTML = serverSvg;
    const svg = svgRef.current.querySelector('svg');
    if (svg) {
      const container = svgRef.current;
      const isServerSizedSvg = svg.dataset.runaiMermaid === 'v2';

      svg.removeAttribute('style');
      svg.style.height = 'auto';
      if (!isServerSizedSvg) {
        fixCjkNodeSizes(svg);
      }
      // After expanding nodes for CJK text, the content may exceed the original viewBox.
      // Recalculate viewBox + set explicit width so max-width:100% can scale the SVG down.
      const bbox = svg.getBBox();
      if (bbox.width > 0 && bbox.height > 0) {
        const pad = 8;
        const vbW = bbox.width + pad * 2;
        const vbH = bbox.height + pad * 2;
        svg.setAttribute('viewBox', `${bbox.x - pad} ${bbox.y - pad} ${vbW} ${vbH}`);
        svg.setAttribute('width', String(Math.ceil(vbW)));
        svg.setAttribute('height', String(Math.ceil(vbH)));

        const layout = getDiagramLayout(vbW, vbH);
        container.dataset.layout = layout;
        if (layout === 'wide') {
          svg.style.maxWidth = 'none';
          svg.style.width = `${Math.ceil(vbW)}px`;
        } else {
          svg.style.maxWidth = '100%';
          svg.style.removeProperty('width');
        }
      } else {
        container.dataset.layout = 'normal';
        svg.style.maxWidth = '100%';
      }
    }
  }, [serverSvg]);

  const handleDiagramClick = useLastCallback(() => {
    const svgEl = svgRef.current?.querySelector('svg');
    if (!svgEl) return;

    const { url, width, height } = svgElementToBlobUrl(svgEl);
    const standalonePhoto: ApiPhoto = {
      mediaType: 'photo',
      id: `mermaid-${Date.now()}`,
      date: Math.floor(Date.now() / 1000),
      blobUrl: url,
      sizes: [{
        type: 'w',
        width,
        height,
      }],
    };
    openMediaViewer({
      standaloneMedia: [standalonePhoto],
      mediaIndex: 0,
      origin: MediaViewerOrigin.PreviewMedia,
    });
  });

  // No server SVG — show raw code as fallback
  if (!serverSvg) {
    return (
      <PeerColorWrapper className="CodeBlock">
        <p className="code-title">Mermaid</p>
        <pre className="code-block">
          {text}
          <CodeOverlay text={text} className="code-overlay" noCopy={noCopy} />
        </pre>
      </PeerColorWrapper>
    );
  }

  return (
    <PeerColorWrapper className={buildClassName(styles.root, 'CodeBlock')}>
      <p className="code-title">Mermaid</p>
      <div
        className={styles.diagram}
        ref={svgRef}
        onClick={handleDiagramClick}
        role="button"
        tabIndex={0}
      />
      <CodeOverlay text={text} className="code-overlay" noCopy={noCopy} />
    </PeerColorWrapper>
  );
};

export default memo(MermaidBlock);
